package com.hyperether.goodjob.scenes.register

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.runtime.Composable
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement

@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun PlatformLogin(
    emailLabel: String,
    emailPlaceholder: String,
    passwordLabel: String,
    passwordPlaceholder: String,
    forgotPasswordLabel: String,
    loginButtonLabel: String,
    onCredentialsEntered: (email: String, password: String) -> Unit,
    onPasswordForgotten: (email: String) -> Unit,
    visibility: Boolean,
    onCreateAccount: () -> Unit
) {

    if (visibility) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(320.dp)
        ) {
            WebElementView(
                factory = {
                    val div = document.createElement("div") as HTMLDivElement
                    div.apply {
                        id = "login-form-${kotlin.js.Date().getTime().toLong()}"
                        style.width = "100%"
                        style.height = "100%"
                        style.asDynamic().pointerEvents = "auto"
                    }

                    // Set up the login form HTML with exact styles from index.html
                    div.innerHTML = """
                        <style>
                            #login-form {
                                display: flex;
                                flex-direction: column;
                                gap: 14px;
                                width: 100%;
                            }

                            .form-group {
                                display: flex;
                                flex-direction: column;
                            }

                            label {
                                font-size: 14px;
                                margin-bottom: 4px;
                                color: #333;
                            }

                            input[type="email"],
                            input[type="password"],
                            input[type="text"] {
                                padding: 12px;
                                border: 1px solid #ccc;
                                border-radius: 16px;
                                font-size: 16px;
                                outline-color: #1976d2;
                                box-sizing: border-box;
                                width: 100%;
                            }

                            input[type="email"]::placeholder {
                                color: #CCCCCC;
                            }
                            input[type="password"]::placeholder {
                                color: #CCCCCC;
                            }
                            input[type="text"]::placeholder {
                                color: #CCCCCC;
                            }

                            .password-input-wrapper {
                                position: relative;
                                width: 100%;
                            }

                            .password-input-wrapper input {
                                width: 100%;
                                padding-right: 40px;
                                box-sizing: border-box;
                            }

                            .password-input-wrapper img {
                                position: absolute;
                                right: 12px;
                                top: 50%;
                                transform: translateY(-50%);
                                width: 16px;
                                height: 16px;
                                cursor: pointer;
                                opacity: 0.6;
                                transition: opacity 0.2s;
                            }

                            .password-input-wrapper img:hover {
                                opacity: 1;
                            }

                            .password-wrapper {
                                position: relative;
                            }

                            .toggle-password {
                                position: absolute;
                                right: 12px;
                                top: 50%;
                                transform: translateY(-50%);
                                background: transparent;
                                border: none;
                                cursor: pointer;
                                font-size: 16px;
                                color: #666;
                            }

                            .forgot-button {
                                align-self: flex-end;
                                background: none;
                                border: none;
                                color: #34618E;
                                cursor: pointer;
                                font-size: 16px;
                                padding: 10px;
                            }

                            .login-button {
                                width: 100%;
                                padding: 14px;
                                background-color: #34618E;
                                color: white;
                                border: none;
                                border-radius: 25px;
                                font-size: 16px;
                                font-weight: 500;
                                cursor: pointer;
                                transition: background-color 0.3s ease;
                            }

                            .login-button:disabled {
                                background-color: #ccc;
                                cursor: not-allowed;
                            }

                            .input-icon-wrapper {
                                position: relative;
                                padding-top: 10px;
                                width: 100%;
                            }

                            .input-icon-wrapper input {
                                width: 100%;
                                padding-left: 42px;
                                padding-right: 42px;
                                height: 54px;
                                box-sizing: border-box;
                                font-size: 18px;
                            }

                            .input-icon {
                                position: absolute;
                                left: 12px;
                                top: 57%;
                                transform: translateY(-50%);
                                width: 24px;
                                height: 24px;
                                pointer-events: none;
                            }

                            .toggle-password-icon {
                                position: absolute;
                                right: 12px;
                                top: 57%;
                                transform: translateY(-50%);
                                width: 18px;
                                height: 18px;
                                cursor: pointer;
                                opacity: 0.6;
                            }

                            .toggle-password-icon:hover {
                                opacity: 1;
                            }
                        </style>

                        <form id="login-form" autocomplete="off">
                            <div class="form-group email-wrapper">
                                <div class="input-icon-wrapper">
                                    <img src="email.svg" alt="Email icon" class="input-icon" />
                                    <input
                                        type="email"
                                        id="email"
                                        name="username"
                                        placeholder="$emailPlaceholder"
                                        autocomplete="username"
                                        readonly
                                        onfocus="this.removeAttribute('readonly');"
                                    />
                                </div>
                            </div>

                            <div class="form-group password-wrapper">
                                <div class="input-icon-wrapper">
                                    <img src="password.svg" alt="Password icon" class="input-icon" />
                                    <input
                                        type="password"
                                        id="password"
                                        name="current-password"
                                        placeholder="$passwordPlaceholder"
                                        autocomplete="current-password"
                                        readonly
                                        onfocus="this.removeAttribute('readonly');"
                                    />
                                    <img
                                        id="toggle-password-icon"
                                        src="eye-off.svg"
                                        alt="Toggle password"
                                        onclick="togglePassword()"
                                        class="toggle-password-icon"
                                    />
                                </div>
                            </div>

                            <button type="button" class="forgot-button" onclick="handleForgotPassword()">$forgotPasswordLabel</button>

                            <button type="submit" class="login-button" disabled>$loginButtonLabel</button>
                        </form>
                    """.trimIndent()

                    // Initialize the login form functionality
                    val emailInput = div.querySelector("#email")
                    val passwordInput = div.querySelector("#password")
                    val loginButton = div.querySelector(".login-button")

                    if (emailInput != null && passwordInput != null && loginButton != null) {
                        emailInput.addEventListener("input", {
                            val emailValue = emailInput.asDynamic().value as String
                            val passwordValue = passwordInput.asDynamic().value as String
                            loginButton.asDynamic().disabled = !(emailValue.isNotEmpty() && passwordValue.isNotEmpty())
                        })

                        passwordInput.addEventListener("input", {
                            val emailValue = emailInput.asDynamic().value as String
                            val passwordValue = passwordInput.asDynamic().value as String
                            loginButton.asDynamic().disabled = !(emailValue.isNotEmpty() && passwordValue.isNotEmpty())
                        })

                        var passwordVisible = false

                        window.asDynamic().togglePassword = {
                            val input = div.querySelector("#password")
                            val icon = div.querySelector("#toggle-password-icon")

                            passwordVisible = !passwordVisible
                            input?.asDynamic()?.type = if (passwordVisible) "text" else "password"
                            icon?.asDynamic()?.src = if (passwordVisible) "eye.svg" else "eye-off.svg"
                        }

                        window.asDynamic().handleForgotPassword = {
                            val email = emailInput.asDynamic().value.toString().trim()
                            onPasswordForgotten(email)
                        }

                        window.asDynamic().handleCreateAccount = {
                            onCreateAccount()
                        }

                        val form = div.querySelector("#login-form")
                        form?.addEventListener("submit", { event ->
                            event.preventDefault()
                            val email = emailInput.asDynamic().value.toString().trim()
                            val password = passwordInput.asDynamic().value.toString().trim()
                            onCredentialsEntered(email, password)
                        })
                    }

                    div
                },
                modifier = Modifier.fillMaxWidth().height(320.dp),
                update = { div ->
                    // Update form if needed
                }
            )
        }
    }
}